import { generatePath } from 'react-router';
import type {
  ListQueryOptions,
  RouteParams,
  RouteQuery,
  RouteQueryBuilder,
} from '@/types/routeGenerators';

const prefix = (urlPrefix?: string | null) => {
  return urlPrefix ? `/${urlPrefix}` : '';
};

const withQuery = (pathname: string, query: RouteQuery) => {
  if (typeof query === 'string') {
    return query ? `${pathname}?${query}` : pathname;
  }

  const searchParams = Object.entries(query)
    .filter((entry) => entry[1] !== null && entry[1] !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return searchParams ? `${pathname}?${searchParams}` : pathname;
};

export const route = (
  path: string,
  makeQuery: RouteQueryBuilder = () => ({}),
  withPrefix = false,
) => {
  return (params: RouteParams = {}) => {
    const paramNames = [...path.matchAll(/:([\w-]+)/g)].map((match) => match[1]);
    const hasPathParams = paramNames.some((name) => params[name] !== null && params[name] !== undefined);
    const placeholders = Object.fromEntries(paramNames.map((name) => [name, `__route_param_${name}__`]));
    const pathParams = Object.fromEntries(
      paramNames.map((name) => [name, params[name] ?? placeholders[name]]),
    );
    const pathname = hasPathParams
      ? Object.entries(placeholders).reduce(
          (generatedPath, [name, placeholder]) => generatedPath.replace(placeholder, `:${name}`),
          generatePath(path, pathParams),
        )
      : path;

    return withQuery(`${withPrefix ? prefix(params.urlPrefix) : ''}${pathname}`, makeQuery(params));
  };
};

export const sourceQuery = ({ source, multipleSources }: RouteParams) => ({
  source: source || null,
  multipleSources: source && multipleSources ? multipleSources : null,
});

export const singleSourceQuery = ({ source }: RouteParams) => ({ source: source || null });

export const listQuery = (
  { searchBy = 'source', includeType = false }: ListQueryOptions = {},
) => {
  return ({ source, multipleSources }: RouteParams) => {
    if (source && multipleSources) {
      return {
        search: `${searchBy}:${multipleSources}`,
        source,
        multipleSources,
      };
    }

    return source
      ? {
          search: `${searchBy}:${source}`,
          source,
          type: includeType ? source : null,
        }
      : {};
  };
};

export const featureQuery = ({ featureType }: RouteParams) => ({ source: featureType || null });

export const featureListQuery = ({ featureType }: RouteParams) => {
  return featureType
    ? {
        search: `type:${featureType}`,
        source: featureType,
        type: featureType,
      }
    : {};
};

export const searchParamsQuery = ({ searchParams }: RouteParams) => searchParams || '';
